import React, { useEffect } from "react";
import { useContext, useState } from "react";
import AuthContext from "../store/AuthContext";
import { get, postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Checkbox,
  Chip,
  Typography,
} from "@mui/material";
import ModalHeader from "./ModalHeader";
import ModalFooter from "./ModalFooter";

const PunishedLeaveModal = ({ setOpenModal, openModal, refresh }) => {
  const [leaveData, setLeaveData] = React.useState({
    user_id: "",
    leave_type: "",
    reason: "",
    days: "",
    type: "ADD",
  });

  const [users, setUsers] = React.useState([]);
  const [request, setRequest] = React.useState(false);
  const [error, setError] = React.useState("");

  const [userLeaveSummary, setUserLeaveSummary] = React.useState(null);

  const TYPE_KEYS = {
    cl: { totalKey: "total_cl", balanceKey: "cl", label: "Casual Leave" },
    sl: { totalKey: "total_sl", balanceKey: "sl", label: "Sick Leave" },
    el: { totalKey: "total_el", balanceKey: "el", label: "Earned Leave" },
    ml: { totalKey: "total_ml", balanceKey: "ml", label: "Maternity Leave" },
    pl: { totalKey: "total_pl", balanceKey: "pl", label: "Paternity Leave" },
    bl: { totalKey: "total_bl", balanceKey: "bl", label: "Bereavement Leave" },
    total_compensatory: {
      totalKey: "total_compensatory",
      balanceKey: "compensatory",
      label: "Compensatory Leave",
    },
    unpaid: { totalKey: null, balanceKey: "unpaid", label: "Unpaid Leave" },
  };

  const leaveTypes = [
    { value: "sl", label: "Sick Leave" },
    { value: "cl", label: "Casual Leave" },
    { value: "el", label: "Earned Leave" },
    { value: "ml", label: "Maternity Leave" },
    { value: "pl", label: "Paternity Leave" },
    { value: "bl", label: "Bereavement Leave" },
    { value: "total_compensatory", label: "Compensatory Leave" },
    { value: "unpaid", label: "Unpaid Leave" },
  ];

  const types = [
    { value: "ADD", label: "Add Leave" },
    { value: "DEDUCT", label: "Deduct Leave" },
  ];

  // ---------- Helpers ----------
  const num = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isNaN(n) ? 0 : n;
  };

  const getUsers = () => {
    postJsonData(
      `${ApiEndpoints.GET_USERS_NAME}`,
      { role: "DEVELOPER" },
      setRequest,
      (res) => {
        if (res.data) setUsers(res.data);
      },
      (error) => {
        console.error("API Error:", error);
        apiErrorToast(error);
      }
    );
  };

  React.useEffect(() => {
    getUsers();
  }, []);
  const fetchUserLeaves = (userName) => {
    if (!userName) return;

       
    const url = `${ApiEndpoints.TOTAL_LEAVE}?user_name=${encodeURIComponent(
      userName
    )}`;

    get(
      url,
      "", // no object here
      setRequest,
      (res) => {
        const payload = Array.isArray(res?.data) ? res.data[0] : res?.data;
        setUserLeaveSummary(payload || null);
      },
      (err) => {
        console.error(err);
        apiErrorToast(err);
        setUserLeaveSummary(null);
      }
    );
  };

  // Compute per-type stats from TOTAL_LEAVE
  const computeTypeStats = (summary, type) => {
    if (!summary || !type || !TYPE_KEYS[type]) return null;
    const { totalKey, balanceKey } = TYPE_KEYS[type];
    const allocated = totalKey ? num(summary[totalKey]) : null; // may be null for unpaid
    const balance = num(summary[balanceKey]); // current balance (as returned by API)
    const used = allocated !== null ? Math.max(0, allocated - balance) : null;

    return {
      allocated, // entitlement
      balance, // current balance (from API)
      used,
      updatedAt: summary.updated_at || summary.updatedAt || null,
    };
  };

  const selectedStats = React.useMemo(() => {
    return computeTypeStats(userLeaveSummary, leaveData.leave_type);
  }, [userLeaveSummary, leaveData.leave_type]);

  // Live projected balance based on action and days
  const projectedBalance = React.useMemo(() => {
    if (!selectedStats) return null;
    const d = num(leaveData.days);
    let pb = selectedStats.balance;
    if (leaveData.type === "ADD") pb = pb + d;
    if (leaveData.type === "DEDUCT") pb = pb - d;
    return pb;
  }, [selectedStats, leaveData.days, leaveData.type]);

  const willGoNegative =
    leaveData.leave_type !== "unpaid" &&
    selectedStats &&
    leaveData.type === "DEDUCT" &&
    projectedBalance !== null &&
    projectedBalance < 0;

  // ---------- Handlers ----------
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setLeaveData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "leave_type") setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const required = ["user_id", "leave_type", "reason", "days", "type"];
    const isValid = required.every(
      (k) => leaveData[k]?.toString().trim() !== ""
    );
    if (!isValid) {
      setError("All fields are required.");
      return;
    }

    const payload = {
      user_id: leaveData.user_id,
      leave_type: leaveData.leave_type,
      reason: leaveData.reason,
      type: leaveData.type,
    };

    // ✅ Fix: your options use "total_compensatory"
    if (leaveData.leave_type === "total_compensatory") {
      payload.days =  Number(leaveData.days);
    } else {
      payload.days = Number(leaveData.days);
    }

    postJsonData(
      ApiEndpoints.PUNISHED_LEAVE,
      payload,
      setRequest,
      (res) => {
        okSuccessToast(res.data.message);
        handleModalClose();
        if (typeof refresh === "function") refresh();
      },
      (err) => apiErrorToast(err),
      true
    );
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setLeaveData({
      user_id: "",
      leave_type: "",
      reason: "",
      days: "",
      type: "ADD",
    });
    setUserLeaveSummary(null);
    setError("");
  };

  // ---------- UI ----------
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 720,
    maxHeight: "86vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    border: "1px solid #e5e7eb",
    boxShadow: 24,
    p: 3,
    borderRadius: 12,
  };

  const renderHeaderChips = () => {
    if (!userLeaveSummary || !userLeaveSummary.data?.length) return null;

    const summary = userLeaveSummary.data[0]; // ✅ actual object

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 2 }}>
        {Object.keys(TYPE_KEYS).map((t) => {
          const { label, totalKey, balanceKey } = TYPE_KEYS[t];
          const total = totalKey ? summary[totalKey] : null;
          const balance = summary[balanceKey];

          return (
            <Chip
              key={t}
              label={`${label}: ${balance}${total ? ` / ${total}` : ""}`}
              color="primary"
              variant="outlined"
            />
          );
        })}
      </Box>
    );
  };

  return (
    <Modal open={openModal} onClose={handleModalClose}>
      <Box sx={style}>
        <ModalHeader
          title="Add / Deduct Leave"
          handleClose={handleModalClose}
        />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* User Dropdown */}
            <Grid item xs={12}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.name}
                onChange={(e, newValue) => {
                  const userId = newValue ? newValue.id : "";
                  const userName = newValue ? newValue.name : "";

                  // Save actual id for payload
                  setLeaveData((prev) => ({ ...prev, user_id: userId }));

                  // Reset summary
                  setUserLeaveSummary(null);

                  // Fetch summary by user_name
                  if (userName) fetchUserLeaves(userName);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Employee"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Header chips with all leave balances */}
            <Grid item xs={12}>
              {renderHeaderChips()}
            </Grid>

            {/* Leave Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Leave Type</InputLabel>
                <Select
                  name="leave_type"
                  value={leaveData.leave_type}
                  label="Leave Type"
                  onChange={handleFieldChange}
                  disabled={!userLeaveSummary}
                >
                  {leaveTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Action */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Action</InputLabel>
                <Select
                  name="type"
                  value={leaveData.type}
                  label="Action"
                  onChange={handleFieldChange}
                >
                  {types.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Days */}
            <Grid item xs={12} md={6}>
              <TextField
                label={
                  selectedStats
                    ? `Days (Balance: ${selectedStats.balance}${
                        selectedStats.allocated !== null
                          ? ` / ${selectedStats.allocated}`
                          : ""
                      })`
                    : "Days"
                }
                type="number"
                name="days"
                fullWidth
                required
                inputProps={{ min: 0, step: "0.5" }}
                value={leaveData.days}
                onChange={handleFieldChange}
                disabled={!leaveData.leave_type}
              />
            </Grid>

            {/* Reason */}
            <Grid item xs={12}>
              <TextField
                label="Reason"
                name="reason"
                fullWidth
                required
                multiline
                rows={2}
                value={leaveData.reason}
                onChange={handleFieldChange}
              />
            </Grid>
          </Grid>

          <ModalFooter
            onClick={handleSubmit}
            btn="Submit"
            disable={
              !leaveData.user_id ||
              !leaveData.leave_type ||
              !leaveData.reason ||
              !leaveData.days ||
              !leaveData.type
            }
          />
        </form>

        {error && (
          <div style={{ color: "red", marginTop: "10px" }}>{error}</div>
        )}
      </Box>
    </Modal>
  );
};

export default PunishedLeaveModal;
